import type { NotificationSetting } from '@/services/notificationSettingsService';
import {
  canEditRecipients,
  canEditTemplate,
  normalizeRecipientConfigForType
} from './config';
import type { RecipientConfigV1, TemplateOverrides } from './types';

export interface NotificationDraftSaveService {
  updateRecipientConfig(notificationType: string, config: RecipientConfigV1 | null): Promise<NotificationSetting>;
  updateOverrides(notificationType: string, overrides: TemplateOverrides): Promise<NotificationSetting>;
  getSetting(notificationType: string): Promise<NotificationSetting | null>;
}

export class NotificationDraftSaveError extends Error {
  latestSetting: NotificationSetting | null;
  originalError: unknown;

  constructor(message: string, latestSetting: NotificationSetting | null, originalError: unknown) {
    super(message);
    this.name = 'NotificationDraftSaveError';
    this.latestSetting = latestSetting;
    this.originalError = originalError;
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Failed to save notification settings';
}

async function refetchLatest(
  service: NotificationDraftSaveService,
  notificationType: string
): Promise<NotificationSetting | null> {
  try {
    return await service.getSetting(notificationType);
  } catch {
    return null;
  }
}

export async function saveNotificationDraft({
  service,
  selectedSetting,
  draftConfig,
  draftOverrides,
  configDirty,
  overridesDirty
}: {
  service: NotificationDraftSaveService;
  selectedSetting: NotificationSetting;
  draftConfig: RecipientConfigV1 | null;
  draftOverrides: TemplateOverrides;
  configDirty: boolean;
  overridesDirty: boolean;
}): Promise<NotificationSetting> {
  const notificationType = selectedSetting.notification_type;
  const textEditable = canEditTemplate(notificationType);
  const shouldSaveConfig = canEditRecipients(notificationType) && configDirty;
  const shouldSaveOverrides = textEditable && overridesDirty;

  if (!shouldSaveConfig && !shouldSaveOverrides) {
    return selectedSetting;
  }

  try {
    if (shouldSaveConfig) {
      await service.updateRecipientConfig(
        notificationType,
        draftConfig
          ? normalizeRecipientConfigForType(notificationType, draftConfig)
          : null
      );
    }
    if (shouldSaveOverrides) {
      await service.updateOverrides(notificationType, {
        subject: draftOverrides.subject,
        body: draftOverrides.body
      });
    }

    return await service.getSetting(notificationType) ?? selectedSetting;
  } catch (error) {
    const latestSetting = await refetchLatest(service, notificationType);
    throw new NotificationDraftSaveError(errorMessage(error), latestSetting, error);
  }
}
